import React, {useMemo} from 'react';

import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {Select, SelectOption} from '../../Select';
import {ConfigElement} from '../types';

interface SortValue {
    id: string;
    desc?: boolean;
}

function compareSelectValues(first: SortValue | null, second: SortValue | null) {
    if (first == null && second == null) {
        return true;
    }

    if (first != null && second != null) {
        return first.id === second.id && first.desc === second.desc;
    }

    return false;
}

export type SortingType = Array<SortValue>;

interface Props {
    config: ConfigElement[];
    onChange: (sorting: SortingType) => void;
    defaultValue?: SortingType; // TODO pass sorting param as default value from model
    className?: string;
}

const configReducer = (config: ConfigElement[], t: TFunction) =>
    config.reduce((accumulator, currentValue) => {
        const desc = {
            value: {id: currentValue.accessor, desc: true},
            label: `${currentValue.parentHeader || ''} ${currentValue.Header}: ${t(
                'components.endlessList.descending',
            )}`,
        };
        const asc = {
            value: {id: currentValue.accessor, desc: false},
            label: `${currentValue.parentHeader || ''} ${currentValue.Header}: ${t(
                'components.endlessList.ascending',
            )}`,
        };
        return accumulator.concat([desc, asc]);
    }, [] as SelectOption<SortValue>[]);

export const Sorting = ({config, onChange, defaultValue, className}: Props) => {
    const {t} = useTranslation();

    const selectConfig = useMemo(() => {
        const options: SelectOption<SortValue>[] = [
            {
                value: null,
                label: t('components.endlessList.chooseSorting'),
            },
        ];
        options.push(...configReducer(config, t));
        const defaultOption =
            defaultValue != null
                ? options.find((elem) => compareSelectValues(elem.value, defaultValue[0]))
                : options[0];

        return {options, defaultOption};
    }, [config, defaultValue, t]);

    const handleChange = (option: SelectOption<SortValue>) => {
        const value = option.value != null ? [option.value] : [];
        onChange(value);
    };

    return (
        <Select
            options={selectConfig.options}
            defaultValue={selectConfig.defaultOption}
            className={className}
            onChange={handleChange}
        />
    );
};
